import { z } from "zod";

const providerSchema = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/u);
const positiveRevisionSchema = z.string().regex(/^[1-9]\d*$/u);

const merchantStorePlatformWireSchema = z
  .object({
    binding_supported: z.boolean(),
    display_name: z.string().min(1).max(80),
    provider: providerSchema,
  })
  .strict();

const merchantStoreBindingWireSchema = z
  .object({
    binding_source: z.enum(["owned", "delegated_import"]),
    can_reauthorize: z.boolean(),
    can_unbind: z.boolean(),
    display_name: z.string().min(1).max(240),
    external_shop_id: z.string().min(1).max(240),
    id: z.string().regex(/^msb_[a-zA-Z0-9_-]+$/u),
    pending_todo_count: z.number().int().nonnegative(),
    provider: providerSchema,
    region: z.string().max(80),
    revision: positiveRevisionSchema,
    risk_count: z.number().int().nonnegative(),
    risk_level: z.enum(["normal", "high"]),
    status: z.enum(["active", "disconnected"]),
    sync_error_code: z.string().max(120),
    sync_status: z.enum([
      "healthy",
      "sync_error",
      "reauthorization_required",
    ]),
    updated_at: z.string().datetime({ offset: true }),
  })
  .strict();

export const merchantStoreBindingsResponseSchema = z
  .object({
    bindings: z.array(merchantStoreBindingWireSchema),
    organization_id: z.string().min(1),
    platforms: z.array(merchantStorePlatformWireSchema),
    schema_version: z.literal(1),
  })
  .strict()
  .transform((response) => ({
    bindings: response.bindings.map((binding) => ({
      bindingSource: binding.binding_source,
      canReauthorize: binding.can_reauthorize,
      canUnbind: binding.can_unbind,
      displayName: binding.display_name,
      externalShopId: binding.external_shop_id,
      id: binding.id,
      pendingTodoCount: binding.pending_todo_count,
      provider: binding.provider,
      region: binding.region,
      revision: binding.revision,
      riskCount: binding.risk_count,
      riskLevel: binding.risk_level,
      status: binding.status,
      syncErrorCode: binding.sync_error_code,
      syncStatus: binding.sync_status,
      updatedAt: binding.updated_at,
    })),
    organizationId: response.organization_id,
    platforms: response.platforms.map((platform) => ({
      bindingSupported: platform.binding_supported,
      displayName: platform.display_name,
      provider: platform.provider,
    })),
    schemaVersion: response.schema_version,
  }));

export const disconnectMerchantStoreBindingResponseSchema = z
  .object({ binding: merchantStoreBindingWireSchema })
  .strict()
  .transform((response) =>
    merchantStoreBindingsResponseSchema.parse({
      bindings: [response.binding],
      organization_id: "disconnect-response",
      platforms: [],
      schema_version: 1,
    }).bindings[0],
  );

export type MerchantStoreBindingsResponse = ReturnType<
  typeof merchantStoreBindingsResponseSchema.parse
>;
export type MerchantStoreBinding =
  MerchantStoreBindingsResponse["bindings"][number];
export type MerchantStorePlatform =
  MerchantStoreBindingsResponse["platforms"][number];
